/**
 * AdminDashboardPage Component
 *
 * Admin overview dashboard showing platform statistics and quick links
 * to all admin tools.
 */

import { type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PeopleIcon from '@mui/icons-material/People';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import BadgeIcon from '@mui/icons-material/Badge';
import FeedbackIcon from '@mui/icons-material/Feedback';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useAdminStats } from '@/hooks/useAdminStats';
import { useNewFeedbackCount } from '@/hooks/useNewFeedbackCount';

/**
 * AdminDashboardPage displays admin overview statistics and navigation
 */
export const AdminDashboardPage = () => {
  const { stats, loading, error, refetch } = useAdminStats();
  const { count: newFeedbackCount } = useNewFeedbackCount();

  if (loading) {
    return (
      <main style={containerStyle}>
        <div style={loadingContainerStyle}>
          <p style={loadingTextStyle}>Loading admin dashboard...</p>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main style={containerStyle}>
        <div style={contentStyle}>
          <div style={errorContainerStyle}>
            <h2 style={errorTitleStyle}>Error Loading Dashboard</h2>
            <p style={errorTextStyle}>{error}</p>
            <button onClick={() => refetch()} style={retryButtonStyle}>
              Try Again
            </button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main style={containerStyle}>
      <div style={contentStyle}>
        {/* Header */}
        <header style={headerStyle}>
          <h1 style={titleStyle}>Admin Tools</h1>
          <p style={subtitleStyle}>Platform management and administration</p>
        </header>

        {/* Quick Links */}
        <section style={linksSectionStyle}>
          <h2 style={sectionTitleStyle}>Admin Tools</h2>
          <div style={linksGridStyle}>
            {/* Gym Requests Link */}
            <Link to="/admin/gym-requests" style={linkCardStyle}>
              <div style={linkIconContainerStyle}>
                <AssignmentIcon style={linkIconStyle} />
              </div>
              <div style={linkContentStyle}>
                <h3 style={linkTitleStyle}>Gym Requests</h3>
                <p style={linkDescriptionStyle}>Review and manage gym registration requests</p>
                {stats && stats.pendingRequests > 0 && (
                  <span style={pendingBadgeStyle}>{stats.pendingRequests} pending</span>
                )}
              </div>
              <ChevronRightIcon style={arrowStyle} />
            </Link>

            {/* Users Link */}
            <Link to="/admin/users" style={linkCardStyle}>
              <div
                style={{ ...linkIconContainerStyle, backgroundColor: 'var(--color-badge-info-bg)' }}
              >
                <PeopleIcon style={{ ...linkIconStyle, color: 'var(--color-badge-info-text)' }} />
              </div>
              <div style={linkContentStyle}>
                <h3 style={linkTitleStyle}>Manage Users</h3>
                <p style={linkDescriptionStyle}>View all users and manage admin privileges</p>
              </div>
              <ChevronRightIcon style={arrowStyle} />
            </Link>

            {/* Create Gym Link */}
            <Link to="/admin/create-gym" style={linkCardStyle}>
              <div
                style={{
                  ...linkIconContainerStyle,
                  backgroundColor: 'var(--color-badge-success-bg)',
                }}
              >
                <AddBusinessIcon
                  style={{ ...linkIconStyle, color: 'var(--color-badge-success-text)' }}
                />
              </div>
              <div style={linkContentStyle}>
                <h3 style={linkTitleStyle}>Create Gym</h3>
                <p style={linkDescriptionStyle}>Create a gym directly (bypasses approval)</p>
              </div>
              <ChevronRightIcon style={arrowStyle} />
            </Link>

            {/* Assign Roles Link */}
            <Link to="/admin/assign-roles" style={linkCardStyle}>
              <div
                style={{
                  ...linkIconContainerStyle,
                  backgroundColor: 'var(--color-badge-warning-bg)',
                }}
              >
                <BadgeIcon style={{ ...linkIconStyle, color: 'var(--color-badge-warning-text)' }} />
              </div>
              <div style={linkContentStyle}>
                <h3 style={linkTitleStyle}>Assign Roles</h3>
                <p style={linkDescriptionStyle}>Assign owner, setter, or climber roles</p>
              </div>
              <ChevronRightIcon style={arrowStyle} />
            </Link>

            {/* Feedback Link */}
            <Link to="/admin/feedback" style={linkCardStyle}>
              <div
                style={{
                  ...linkIconContainerStyle,
                  backgroundColor: 'var(--color-badge-purple-bg)',
                }}
              >
                <FeedbackIcon
                  style={{ ...linkIconStyle, color: 'var(--color-badge-purple-text)' }}
                />
              </div>
              <div style={linkContentStyle}>
                <h3 style={linkTitleStyle}>User Feedback</h3>
                <p style={linkDescriptionStyle}>View and respond to user feedback</p>
                {newFeedbackCount > 0 && (
                  <span style={feedbackBadgeStyle}>{newFeedbackCount} new</span>
                )}
              </div>
              <ChevronRightIcon style={arrowStyle} />
            </Link>

            {/* Flyer Generator Link */}
            <Link to="/admin/flyer-generator" style={linkCardStyle}>
              <div style={{ ...linkIconContainerStyle, backgroundColor: '#fce7f3' }}>
                <span style={{ ...linkIconStyle, color: '#9d174d' }}>P</span>
              </div>
              <div style={linkContentStyle}>
                <h3 style={linkTitleStyle}>Flyer Generator</h3>
                <p style={linkDescriptionStyle}>Generate branded marketing flyers for gyms</p>
              </div>
              <span style={arrowStyle}>→</span>
            </Link>
          </div>
        </section>
      </div>
    </main>
  );
};

/**
 * Component styles
 */
const containerStyle: CSSProperties = {
  minHeight: '100%',
  backgroundColor: 'var(--color-bg-secondary)',
};

const contentStyle: CSSProperties = {
  maxWidth: '900px',
  margin: '0 auto',
  padding: '20px',
};

const loadingContainerStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100%',
};

const loadingTextStyle: CSSProperties = {
  fontSize: '1.125rem',
  color: 'var(--color-text-secondary)',
};

const errorContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '40px 20px',
  textAlign: 'center',
  backgroundColor: 'var(--color-bg-primary)',
  borderRadius: '12px',
  border: '1px solid var(--color-border)',
};

const errorTitleStyle: CSSProperties = {
  fontSize: '1.25rem',
  fontWeight: 600,
  color: 'var(--color-text-primary)',
  marginBottom: '8px',
};

const errorTextStyle: CSSProperties = {
  fontSize: '1rem',
  color: 'var(--color-text-secondary)',
  marginBottom: '16px',
};

const retryButtonStyle: CSSProperties = {
  padding: '12px 24px',
  backgroundColor: 'var(--color-primary)',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  fontSize: '1rem',
  fontWeight: 500,
  cursor: 'pointer',
};

const headerStyle: CSSProperties = {
  marginBottom: '32px',
};

const titleStyle: CSSProperties = {
  fontSize: '2rem',
  fontWeight: 700,
  color: 'var(--color-text-primary)',
  margin: '0 0 8px 0',
};

const subtitleStyle: CSSProperties = {
  fontSize: '1rem',
  color: 'var(--color-text-secondary)',
  margin: 0,
};

const sectionTitleStyle: CSSProperties = {
  fontSize: '0.875rem',
  fontWeight: 600,
  color: 'var(--color-text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  margin: '0 0 16px 0',
};

const linksSectionStyle: CSSProperties = {
  marginBottom: '32px',
};

const linksGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  gap: '16px',
};

const linkCardStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  padding: '20px',
  backgroundColor: 'var(--color-bg-primary)',
  borderRadius: '12px',
  border: '1px solid var(--color-border)',
  textDecoration: 'none',
  transition: 'transform 0.2s, box-shadow 0.2s',
  cursor: 'pointer',
};

const linkIconContainerStyle: CSSProperties = {
  width: '48px',
  height: '48px',
  borderRadius: '12px',
  backgroundColor: 'var(--color-badge-warning-bg)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const linkIconStyle: CSSProperties = {
  fontSize: '1.5rem',
  color: 'var(--color-badge-warning-text)',
};

const linkContentStyle: CSSProperties = {
  flex: 1,
};

const linkTitleStyle: CSSProperties = {
  fontSize: '1rem',
  fontWeight: 600,
  color: 'var(--color-text-primary)',
  margin: '0 0 4px 0',
};

const linkDescriptionStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--color-text-secondary)',
  margin: 0,
};

const pendingBadgeStyle: CSSProperties = {
  display: 'inline-block',
  marginTop: '8px',
  padding: '4px 10px',
  fontSize: '0.75rem',
  fontWeight: 600,
  backgroundColor: 'var(--color-badge-warning-bg)',
  color: 'var(--color-badge-warning-text)',
  borderRadius: '9999px',
};

const feedbackBadgeStyle: CSSProperties = {
  display: 'inline-block',
  marginTop: '8px',
  padding: '4px 10px',
  fontSize: '0.75rem',
  fontWeight: 600,
  backgroundColor: 'var(--color-badge-info-bg)',
  color: 'var(--color-badge-info-text)',
  borderRadius: '9999px',
};

const arrowStyle: CSSProperties = {
  fontSize: '1.25rem',
  color: 'var(--color-text-muted)',
};

export default AdminDashboardPage;
